import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { NgClass } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AppSvgIconComponent,
  OverlayService,
  PaginationComponent,
  type PaginationEvent,
  SignalButton,
  SignalTextField,
  State,
} from '@vallift/ngl';
import { BaseComponent } from '@core/base/base-component';
import { ConfirmOverlayComponent } from '@core/components/confirm-overlay/confirm-overlay';
import { WorkspaceAPIService } from '@core/api/workspace/services/workspace-api/workspaceAPI.service';
import type { ContactGroupMemberResponse } from '@core/api/workspace/models/contactGroupMemberResponse';
import type { ContactGroupResponse } from '@core/api/workspace/models/contactGroupResponse';
import type { DeleteContactGroupApiResponse } from '@core/api/workspace/models/deleteContactGroupApiResponse';
import type { ListContactGroupMembersApiResponse } from '@core/api/workspace/models/listContactGroupMembersApiResponse';
import type { ListContactGroupsApiResponse } from '@core/api/workspace/models/listContactGroupsApiResponse';
import type { RemoveContactGroupMemberApiResponse } from '@core/api/workspace/models/removeContactGroupMemberApiResponse';
import {
  AddMembersOverlayComponent,
  type AddMembersOverlayResult,
} from '../add-members-overlay/add-members-overlay';
import {
  CreateGroupOverlayComponent,
  type CreateGroupOverlayResult,
} from '../create-group-overlay/create-group-overlay';
import {
  EditGroupOverlayComponent,
  type EditGroupOverlayResult,
} from '../edit-group-overlay/edit-group-overlay';
import { PeopleToolbarService } from '../people-toolbar.service';
import {
  resolveAvatarColorClass,
  resolveInitials,
} from '@core/utils/initial-avatar.util';

@Component({
  selector: 'app-groups-page',
  standalone: true,
  imports: [
    AppSvgIconComponent,
    NgClass,
    PaginationComponent,
    SignalButton,
    SignalTextField,
  ],
  templateUrl: './groups-page.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'page-fill' },
})
export class GroupsPage extends BaseComponent implements OnInit {
  private readonly userAPIService = inject(WorkspaceAPIService);
  private readonly overlayService = inject(OverlayService);
  private readonly toolbar = inject(PeopleToolbarService);
  private readonly destroyRef = inject(DestroyRef);

  readonly listState = new State<ListContactGroupsApiResponse>();
  readonly membersState = new State<ListContactGroupMembersApiResponse>();
  readonly deleteState = new State<DeleteContactGroupApiResponse>();
  readonly removeState = new State<RemoveContactGroupMemberApiResponse>();

  readonly groups = signal<ContactGroupResponse[]>([]);
  readonly members = signal<ContactGroupMemberResponse[]>([]);
  readonly selectedGroup = signal<ContactGroupResponse | null>(null);

  readonly pageNumber = signal(1);
  readonly pageSize = signal(25);
  readonly totalCount = signal(0);
  readonly paginationKey = signal(0);

  private searchText = '';

  ngOnInit(): void {
    this.loadItems();
    this.toolbar.createClicks$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.onCreate();
      });
  }

  onSearch(text: string): void {
    this.searchText = text?.trim() ?? '';
    this.pageNumber.set(1);
    this.paginationKey.update((key) => key + 1);
    this.loadItems();
  }

  onPageChange(event: PaginationEvent): void {
    this.pageNumber.set(event.pageNumber);
    this.pageSize.set(event.pageSize);
    this.loadItems();
  }

  onRowClicked(item: ContactGroupResponse): void {
    if (!item?.id) {
      return;
    }
    this.selectedGroup.set(item);
    this.loadMembers(item.id);
  }

  onCreate(): void {
    const dialogRef = this.overlayService.openModal(CreateGroupOverlayComponent);

    dialogRef.closed.subscribe(
      (result: CreateGroupOverlayResult | null | undefined) => {
        if (!result?.id) {
          return;
        }
        this.selectedGroup.set(result);
        this.members.set([]);
        this.pageNumber.set(1);
        this.paginationKey.update((key) => key + 1);
        this.loadItems();
        this.openAddMembersForGroup(result);
      },
    );
  }

  onEdit(event: Event, item: ContactGroupResponse): void {
    event.stopPropagation();
    this.openEditGroup(item);
  }

  onDelete(event: Event, item: ContactGroupResponse): void {
    event.stopPropagation();
    this.openDeleteGroup(item);
  }

  onEditSelected(): void {
    const group = this.selectedGroup();
    if (!group) {
      return;
    }
    this.openEditGroup(group);
  }

  onDeleteSelected(): void {
    const group = this.selectedGroup();
    if (!group) {
      return;
    }
    this.openDeleteGroup(group);
  }

  onAddMembers(): void {
    const group = this.selectedGroup();
    if (!group?.id) {
      return;
    }
    this.openAddMembersForGroup(group);
  }

  private openAddMembersForGroup(group: { id: string; name: string }): void {
    const dialogRef = this.overlayService.openModal(AddMembersOverlayComponent, {
      data: {
        groupId: group.id,
        groupName: group.name,
        initialMembers: this.members().map((m) => ({
          id: m.contactId,
          firstName: m.firstName,
          lastName: m.lastName,
          email: m.email,
        })),
      },
      panelClass: [
        'add-members-overlay-panel',
        'rounded-3xl',
        'overflow-hidden',
        'w-auto',
        'max-w-[calc(100%-2rem)]',
      ],
    });

    dialogRef.closed.subscribe(
      (result: AddMembersOverlayResult | null | undefined) => {
        if (!result) {
          return;
        }
        this.loadMembers(group.id);
        this.loadItems();
      },
    );
  }

  onRemoveMember(member: ContactGroupMemberResponse): void {
    const groupId = this.selectedGroup()?.id;
    if (!groupId) {
      return;
    }

    const displayName = this.memberDisplayName(member);
    const dialogRef = this.overlayService.openModal(ConfirmOverlayComponent, {
      data: {
        title: 'Remove from group',
        message: `Remove “${displayName}” from this group?`,
        confirmLabel: 'Remove',
      },
      panelClass: ['confirm-overlay-panel'],
      fitContent: true,
    });

    dialogRef.closed.subscribe((confirmed: boolean | undefined) => {
      if (!confirmed) {
        return;
      }

      this.executeRequest({
        state: this.removeState,
        request: this.userAPIService.removeContactGroupMember(
          groupId,
          member.contactId,
        ),
        showLoader: true,
        handleError: true,
        onSuccess: () => {
          this.toasterService.success('Contact removed from group.');
          this.loadMembers(groupId);
          this.loadItems();
        },
      });
    });
  }

  membersLabel(count: number): string {
    return count === 1 ? '1 member' : `${count} members`;
  }

  memberDisplayName(member: ContactGroupMemberResponse): string {
    return [member.firstName, member.lastName].filter(Boolean).join(' ');
  }

  avatarColor(id: string): string {
    return resolveAvatarColorClass(id);
  }

  groupInitials(name: string): string {
    return resolveInitials({ name });
  }

  memberInitials(member: ContactGroupMemberResponse): string {
    return resolveInitials({
      firstName: member.firstName,
      lastName: member.lastName,
      email: member.email,
    });
  }

  private openEditGroup(item: ContactGroupResponse): void {
    const dialogRef = this.overlayService.openModal(EditGroupOverlayComponent, {
      data: {
        id: item.id,
        name: item.name,
        description: item.description,
      },
    });

    dialogRef.closed.subscribe(
      (result: EditGroupOverlayResult | null | undefined) => {
        if (!result) {
          return;
        }
        this.loadItems();
      },
    );
  }

  private openDeleteGroup(item: ContactGroupResponse): void {
    const dialogRef = this.overlayService.openModal(ConfirmOverlayComponent, {
      data: {
        title: 'Delete group',
        message: `Are you sure you want to delete “${item.name}”? Members will be removed from this group.`,
        confirmLabel: 'Delete',
      },
      panelClass: ['confirm-overlay-panel'],
      fitContent: true,
    });

    dialogRef.closed.subscribe((confirmed: boolean | undefined) => {
      if (!confirmed) {
        return;
      }

      this.executeRequest({
        state: this.deleteState,
        request: this.userAPIService.deleteContactGroup(item.id),
        showLoader: true,
        handleError: true,
        onSuccess: () => {
          this.toasterService.success('Group deleted.');
          if (this.selectedGroup()?.id === item.id) {
            this.selectedGroup.set(null);
            this.members.set([]);
          }
          this.loadItems();
        },
      });
    });
  }

  private loadItems(): void {
    const search = this.searchText.trim();
    const selectedId = this.selectedGroup()?.id;

    this.executeRequest({
      state: this.listState,
      request: this.userAPIService.listContactGroups({
        ...(search ? { search } : {}),
        pageNumber: this.pageNumber(),
        pageSize: this.pageSize(),
      }),
      handleError: true,
      onSuccess: (response) => {
        const items = response.data.items ?? [];
        this.groups.set(items);
        this.totalCount.set(response.data.totalCount ?? 0);

        if (selectedId) {
          const refreshed = items.find((group) => group.id === selectedId);
          if (refreshed) {
            this.selectedGroup.set(refreshed);
          }
        }
      },
    });
  }

  private loadMembers(groupId: string): void {
    this.executeRequest({
      state: this.membersState,
      request: this.userAPIService.listContactGroupMembers(groupId, {
        pageNumber: 1,
        pageSize: 100,
      }),
      handleError: true,
      onSuccess: (response) => {
        this.members.set(response.data.items ?? []);
      },
    });
  }
}
